/**
 * Wind-Drift Visualizer page — the v140 wedge moment.
 *
 * 3D rotatable scene comparing 1-2 builds under user-defined wind,
 * altitude, and shot scenario. The visual is the engine; everything
 * else on the page is in service of making the visual answer the
 * question "what is my build doing at this range in this wind."
 */

import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { encodeStateToHash, decodeStateFromHash, copyShareUrl } from './urlState';
import type { Build, Environment } from '../lib/trajectory/types';
import { solveTrajectory } from '../lib/trajectory';
import { computeForgeScore } from '../lib/forgeScore';
import { BuildPicker } from './inputs/BuildPicker';
import { EnvironmentControls } from './inputs/EnvironmentControls';
import { ScorePanel } from './inputs/ScorePanel';
import { ScenarioSelector } from './inputs/ScenarioSelector';
import { StarterScenarios, type Scenario } from './scenarios/StarterScenarios';
import { OptimizerPanel } from './optimizer/OptimizerPanel';
import { WelcomeModal } from './WelcomeModal';
import './wind-drift.css';

const Scene3D = lazy(() => import('./scene/Scene'));

const DEFAULT_BUILD_A: Build = {
  arrowGrains: 460,
  arrowLengthIn: 28.5,
  spine: 300,
  fOCPercent: 13,
  pointGrains: 100,
  pointType: 'COC fixed',
  insertGrains: 50,
  vaneGeometry: 'DCA Sabre',
  vaneOffsetDeg: 2,
  vaneCount: 3,
  bowIboFps: 342,
  bowDrawWeightLb: 70,
  bowDrawLengthIn: 29,
  tuneQualityYawDeg: 2,
};

const DEFAULT_BUILD_B: Build = {
  arrowGrains: 510,
  arrowLengthIn: 28.5,
  spine: 250,
  fOCPercent: 17,
  pointGrains: 150,
  pointType: 'single-bevel',
  insertGrains: 75,
  vaneGeometry: 'AAE Talon',
  vaneOffsetDeg: 3,
  vaneCount: 3,
  bowIboFps: 342,
  bowDrawWeightLb: 70,
  bowDrawLengthIn: 29,
  tuneQualityYawDeg: 2,
};

const DEFAULT_ENV: Environment = {
  windMph: 12,
  windAngleDeg: 90,
  altitudeFt: 0,
  shotElevationDeg: 0,
  shotScenario: 'broadside-near',
};

export function WindDriftPage() {
  /* On first load, restore state from URL hash if present */
  const initial = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return decodeStateFromHash(window.location.hash);
  }, []);

  const [buildA, setBuildA] = useState<Build>(initial?.a ?? DEFAULT_BUILD_A);
  const [buildB, setBuildB] = useState<Build>(initial?.b ?? DEFAULT_BUILD_B);
  const [env, setEnv] = useState<Environment>(initial?.e ?? DEFAULT_ENV);
  const [rangeYards, setRangeYards] = useState(initial?.r ?? 60);
  const [compareMode, setCompareMode] = useState<boolean>(initial?.b !== null);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [welcomeForceOpen, setWelcomeForceOpen] = useState(false);

  /* Sync state → URL hash (replaceState — no history pollution) */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = encodeStateToHash({ v: 1, a: buildA, b: compareMode ? buildB : null, e: env, r: rangeYards });
    window.history.replaceState(null, '', window.location.pathname + hash);
  }, [buildA, buildB, env, rangeYards, compareMode]);

  const trajA = useMemo(
    () => solveTrajectory(buildA, env, { rangeYards, gates: [20, 40, 60, 80, 100] }),
    [buildA, env, rangeYards]
  );
  const trajB = useMemo(
    () => solveTrajectory(buildB, env, { rangeYards, gates: [20, 40, 60, 80, 100] }),
    [buildB, env, rangeYards]
  );

  const scoreA = useMemo(
    () => computeForgeScore({ build: buildA, env, dist: { rangeYards } }),
    [buildA, env, rangeYards]
  );
  const scoreB = useMemo(
    () => computeForgeScore({ build: buildB, env, dist: { rangeYards } }),
    [buildB, env, rangeYards]
  );

  return (
    <div className="wind-drift-page">
      <WelcomeModal
        forceOpen={welcomeForceOpen}
        onClose={() => setWelcomeForceOpen(false)}
      />
      <nav className="forge-nav">
        <a href="https://sparrowexpeditions.com/pages/the-forge" target="_top">The Forge</a>
        <a href="/wind-drift.html" className="active">Wind-Drift</a>
        <a href="/build-review.html">Build Review</a>
        <a href="/tune.html">Tune Wizard</a>
        <a href="/range.html">Range Log</a>
        <a href="/bows.html">My Bows</a>
        <a href="/arrows.html">My Arrows</a>
        <a href="/pages/methods#ashby" target="_blank" rel="noopener">Methods</a>
        <a href="/blogs/field-notes/500-grain-eland-what-ashby-missed" target="_blank" rel="noopener">Field Notes</a>
        <button
          type="button"
          className="forge-nav-help"
          onClick={() => setWelcomeForceOpen(true)}
          title="How does this work?"
        >
          ?
        </button>
      </nav>

      <header className="wind-drift-header">
        <div className="wind-drift-title-row">
          <div>
            <h1>Wind-Drift Visualizer</h1>
            <p className="lede">
              Compare two builds in 3D. Wind, altitude, tune quality. The math is
              calibrated against documented hunts —{' '}
              <a href="/pages/methods#ashby" target="_blank" rel="noopener">see the methods page</a>.
            </p>
          </div>
          <div className="header-buttons">
            <button
              type="button"
              className="share-button"
              onClick={() => {
                copyShareUrl({ v: 1, a: buildA, b: compareMode ? buildB : null, e: env, r: rangeYards })
                  .then(() => {
                    setCopyToast('Scenario URL copied');
                    setTimeout(() => setCopyToast(null), 2200);
                  })
                  .catch(() => {
                    setCopyToast('Copy failed — check clipboard permissions');
                    setTimeout(() => setCopyToast(null), 2200);
                  });
              }}
              title="Copy a sharable URL for this scenario"
            >
              Share
            </button>
            <button
              type="button"
              className="reset-button"
              onClick={() => {
                setBuildA(DEFAULT_BUILD_A);
                setBuildB(DEFAULT_BUILD_B);
                setEnv(DEFAULT_ENV);
                setRangeYards(60);
                setCompareMode(true);
              }}
              title="Reset all inputs to defaults"
            >
              Reset
            </button>
          </div>
        </div>
        {copyToast && <div className="copy-toast">{copyToast}</div>}
        <StarterScenarios
          onLoad={(s: Scenario) => {
            setBuildA(s.buildA);
            setBuildB(s.buildB);
            setEnv(s.env);
            setRangeYards(s.rangeYards);
            setCompareMode(true);
          }}
        />
      </header>

      <div className="wind-drift-layout">
        <aside className="wind-drift-inputs">
          <BuildPicker
            label="Build A"
            color="ember"
            build={buildA}
            onChange={setBuildA}
          />
          {compareMode && (
            <BuildPicker
              label="Build B"
              color="cyan"
              build={buildB}
              onChange={setBuildB}
            />
          )}
          <button
            type="button"
            className="compare-toggle"
            onClick={() => setCompareMode(!compareMode)}
          >
            {compareMode ? 'Single build' : '+ Add second build'}
          </button>

          <EnvironmentControls
            env={env}
            rangeYards={rangeYards}
            onEnvChange={setEnv}
            onRangeChange={setRangeYards}
          />

          <ScenarioSelector
            value={env.shotScenario}
            onChange={(scenario) => setEnv({ ...env, shotScenario: scenario })}
          />
        </aside>

        <main className="wind-drift-scene">
          <Suspense fallback={<div className="scene-loading">Loading 3D scene…</div>}>
            <Scene3D
              trajA={trajA}
              trajB={compareMode ? trajB : null}
              env={env}
              rangeYards={rangeYards}
            />
          </Suspense>
        </main>

        <aside className="wind-drift-scores">
          <div className="photo-detect-teaser">
            <strong>Tune from photo</strong>
            Sign in and upload a flight photo from your Range Log — OpenCV measures the
            actual impact yaw and replaces the slider. The score becomes &quot;measured&quot;
            instead of &quot;estimated.&quot;
          </div>
          <ScorePanel
            label="Build A"
            color="ember"
            score={scoreA}
            env={env}
            build={buildA}
            onBuildChange={(patch) => setBuildA({ ...buildA, ...patch })}
          />
          {compareMode && (
            <ScorePanel
              label="Build B"
              color="cyan"
              score={scoreB}
              env={env}
              build={buildB}
              onBuildChange={(patch) => setBuildB({ ...buildB, ...patch })}
            />
          )}
        </aside>
      </div>

      <OptimizerPanel
        buildA={buildA}
        buildB={compareMode ? buildB : null}
        env={env}
        onArrowAGrainsChange={(g) => setBuildA({ ...buildA, arrowGrains: g })}
        onArrowBGrainsChange={(g) => setBuildB({ ...buildB, arrowGrains: g })}
      />
    </div>
  );
}
